import { format, startOfMonth, subDays } from "date-fns";
import type {
  AdType,
  ListingListedFor,
  UserListingNotifications,
  PartialProcessedListingDataForNotifications
} from "../../@types/sharedSchema";

export type GroupedListingNotifications = Omit<UserListingNotifications, "startOfWeek"> & {
  date: string;
};

export function groupListingNotificationsByCreatedTime(
  userListingNotifications: UserListingNotifications[],
  adTypeFilter: AdType | "all" = "all",
  listedForFilter: ListingListedFor | "all" = "all"
): GroupedListingNotifications[] {
  const listingNotifications = new Map<string, PartialProcessedListingDataForNotifications>();

  for (const userListingNotification of userListingNotifications) {
    for (const listing of userListingNotification.listingNotifications) {
      if (!listingNotifications.has(listing.id)) {
        if (adTypeFilter !== "all" && listing.adType !== adTypeFilter) continue;

        // Notifications saved before rent support have no listingListedFor — they are all sale
        if (listedForFilter !== "all" && (listing.listingListedFor ?? "sale") !== listedForFilter)
          continue;

        listingNotifications.set(listing.id, listing);
      }
    }
  }

  const monthlyGroups = new Map<string, PartialProcessedListingDataForNotifications[]>();

  for (const listingNotification of listingNotifications.values()) {
    const startOfMonthDate = startOfMonth(new Date(listingNotification.createdTime));

    const dateKey = format(startOfMonthDate, "yyyy-MM-dd");

    if (!monthlyGroups.has(dateKey)) {
      monthlyGroups.set(dateKey, []);
    }

    monthlyGroups.get(dateKey).push(listingNotification);
  }

  const groupedListingNotifications = Array.from(monthlyGroups.entries())
    .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
    .map(([date, listingNotifications]) => {
      listingNotifications.sort((a, b) => b.createdTime.getTime() - a.createdTime.getTime());

      return { date, listingNotifications } as GroupedListingNotifications;
    });

  return groupedListingNotifications;
}

export function getEpochTimeFromDaysAgo(daysAgo: number): number {
  if (typeof daysAgo !== "number" || daysAgo < 0 || !isFinite(daysAgo)) {
    throw new Error("daysAgo must be a non-negative finite number");
  }

  const date = subDays(new Date(), daysAgo);

  return date.getTime();
}
